import React, { PureComponent } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListOnScrollProps, VariableSizeList as List } from 'react-window';
import { Container, LoadingContainer } from './elements';
import { MessageProps } from './types';
import RowRenderer from './RowRenderer';
import { LoadingIndicator } from '../../atoms/LoadingIndicator';

type Props = {
  userId: string;
  messages: Array<MessageProps>;
  convoId: string;
  lastReadMessage: string;
  profileImgSrc: string;
  profileImgLabel: string;
  isSendingMessage: boolean;
  isPaginating: boolean;
  hasNextPage?: boolean;
  isAdmin?: boolean;
  loadMoreMessage: () => void;
  onPressImage: (imageSrc: string, imageFilename: string) => void;
  onDownloadFile: (url: string, filename: string) => void;
};

type State = {
  shouldScrollToBottom: boolean;
  offset: number;
  setIndexRender?: number;
};
class MessageConversation extends PureComponent<Props, State> {
  /*eslint-disable */
  listRef: any;
  rowHeights: any;
  innerRef: any;
  /*eslint-enable */

  constructor(props: Props) {
    super(props);
    this.state = {
      shouldScrollToBottom: false,
      offset: 99999999,
    };
    this.listRef = React.createRef();
    this.rowHeights = React.createRef();
    this.innerRef = React.createRef();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { messages, convoId, isSendingMessage } = this.props;
    const { shouldScrollToBottom } = this.state;

    if (prevProps.convoId !== convoId && !!convoId) {
      this.setState({ offset: 99999999 }, () =>
        this.handleShouldScrollToBottom(true),
      );
    }

    if (
      this.innerRef.current?.scrollHeight &&
      this.state.offset !== this.innerRef.current?.scrollHeight &&
      this.innerRef.current?.scrollHeight > 0
    ) {
      this.setState({ offset: this.innerRef.current?.scrollHeight });
    }

    if (!prevState.setIndexRender && this.state.setIndexRender) {
      this.scrollToItem(messages.length, true);
    }

    if (
      (prevProps.messages.length === 0 && messages.length > 1) ||
      this.state.shouldScrollToBottom
    ) {
      this.scrollToBottom();
    }

    if (
      // after paginating
      messages.length !== prevProps.messages.length &&
      !shouldScrollToBottom
    ) {
      const index = messages.length - prevProps.messages.length;
      this.scrollToItem(index);
    }

    if (prevProps.isSendingMessage && !isSendingMessage) {
      this.scrollToBottom();
    }
  }

  handleShouldScrollToBottom = (val: boolean) => {
    this.setState({ shouldScrollToBottom: val });
  };

  scrollToBottom = () => {
    const { messages } = this.props;
    this.scrollToItem(messages.length, true);
  };

  scrollToItem = (itemLength: number, scrollToBottom = false) => {
    if (scrollToBottom) {
      this.listRef.current?.scrollToItem(itemLength, 'end');
    } else {
      this.listRef.current?.scrollToItem(itemLength, 'smart');
    }
  };

  setRowHeight = (index: number, size: number) => {
    this.listRef.current?.resetAfterIndex(0);
    this.rowHeights.current = { ...this.rowHeights.current, [index]: size };
    if (this.props.messages.length - 1 === index) {
      this.setState({ setIndexRender: index });
    }
  };

  getRowHeight = (index: number) => {
    return this.rowHeights.current?.[index] + 8 || 150;
  };

  handleScroll = (e: ListOnScrollProps) => {
    const { loadMoreMessage, isPaginating } = this.props;
    if (e.scrollOffset === 0 && !isPaginating) {
      loadMoreMessage();
      this.handleShouldScrollToBottom(false);
    }
  };

  render() {
    const {
      messages,
      userId,
      onPressImage,
      onDownloadFile,
      isPaginating,
      profileImgSrc,
      profileImgLabel,
      isAdmin,
    } = this.props;

    return (
      <Container>
        <AutoSizer>
          {({ height, width }) =>
            messages &&
            this.innerRef &&
            this.rowHeights && (
              <List
                height={height}
                itemCount={messages.length}
                ref={this.listRef}
                innerRef={this.innerRef}
                itemSize={this.getRowHeight}
                width={width}
                initialScrollOffset={this.state.offset}
                onScroll={this.handleScroll}
                overscanCount={7}>
                {({
                  index,
                  style,
                }: {
                  index: number;
                  style: React.CSSProperties;
                }) => (
                  <>
                    {isPaginating && index === 0 ? (
                      <LoadingContainer>
                        <LoadingIndicator />
                      </LoadingContainer>
                    ) : null}
                    <RowRenderer
                      userId={userId}
                      index={index}
                      style={style}
                      messages={messages}
                      setRowHeight={this.setRowHeight}
                      onPressImage={onPressImage}
                      onDownloadFile={onDownloadFile}
                      profileImgSrc={profileImgSrc}
                      profileImgLabel={profileImgLabel}
                      isAdmin={isAdmin}
                    />
                  </>
                )}
              </List>
            )
          }
        </AutoSizer>
      </Container>
    );
  }
}

export default MessageConversation;
